<template>
  <div class="guide-packages">
    <h2>What is a package?</h2>
    <p>
      A package is the aggregate root of the signing system. It is the container that
      holds documents, signers, and signing fields together as a single signing
      transaction. One package equals one signing transaction, regardless of how many
      documents or signers are involved.
    </p>
    <p>
      Think of a package as an envelope. You place your documents inside, address it to
      the signers, mark where each person needs to sign, and then send it out. The
      package tracks the entire lifecycle from draft through completion.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Package lifecycle</h2>
    <p>
      Every package moves through a series of states. Understanding this lifecycle
      matters for building integrations that respond correctly to signing events.
    </p>

    <GuideCodeTabs :samples="samples.lifecycle" />

    <h3>Active states</h3>
    <ul>
      <li>
        <strong>Draft.</strong> The package is being assembled. Documents, signers, and
        fields can be added, modified, or removed. Nothing has been sent to anyone yet.
      </li>
      <li>
        <strong>Scheduled.</strong> A send request was issued with a future
        <code>scheduledSendTime</code>. The package holds here until that time, at
        which point it transitions to Sent. Cancel via
        <code>POST /packages/{id}/cancel-schedule</code> to revert to Draft.
      </li>
      <li>
        <strong>Queued.</strong> A send was issued but one or more documents are still
        processing (PDF conversion, page-image generation, tag detection). The package
        is automatically promoted to Sent once processing completes.
      </li>
      <li>
        <strong>Sent.</strong> The package has been finalized and notifications have
        been dispatched. Documents are locked from further editing. The signer list
        and annotations are also locked.
      </li>
      <li>
        <strong>Viewed.</strong> At least one signer has opened the signing link and
        viewed the documents. No fields have been completed yet.
      </li>
      <li>
        <strong>InProgress.</strong> At least one signer has started filling in or
        signing fields. The signing ceremony is actively underway.
      </li>
      <li>
        <strong>Completed.</strong> All signers finished. The final signed PDF and the
        Certificate of Completion are ready for download. This is a terminal state.
      </li>
    </ul>

    <h3>Terminal states</h3>
    <ul>
      <li>
        <strong>Voided.</strong> The sender (or an admin) cancelled the package via
        <code>POST /packages/{id}/void</code>. All signer access is revoked
        immediately.
      </li>
      <li>
        <strong>Declined.</strong> A signer explicitly refused to sign. The entire
        package is terminated and other signers are notified.
      </li>
      <li>
        <strong>Expired.</strong> The package passed its <code>expirationDate</code>
        without all signers completing. No further signing activity is allowed.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Working with packages in the portal</h2>
    <p>
      You do not need to write code to use eSign Launchpad. The portal provides a full
      package-authoring experience that covers everything the API does.
    </p>

    <h3>Create a package</h3>
    <ol>
      <li>Sign in to the portal and click <strong>Packages</strong> in the left sidebar.</li>
      <li>Click <strong>New Package</strong>. Give it a name, optionally set an expiration date and email subject.</li>
      <li>
        <strong>Upload documents.</strong> Drag-and-drop PDF or DOCX files, or click to
        browse. Each file can be up to 25 MB. DOCX files are converted to PDF on upload.
      </li>
      <li><strong>Add signers.</strong> Enter each signer's name and email. Optionally set a routing order to enforce a signing sequence.</li>
      <li>
        <strong>Place signing fields.</strong> Drag fields such as signature, initials,
        date, text, or checkbox from the toolbar onto the document pages. Each field is
        assigned to a specific signer.
      </li>
      <li><strong>Send.</strong> Click <strong>Send for Signature</strong> when ready. Signers receive email notifications immediately.</li>
    </ol>

    <h3>Track, manage, and clean up</h3>
    <ul>
      <li><strong>View status</strong> from the Packages list. Each package shows its current state (Draft, Sent, InProgress, Completed, and so on) and last activity.</li>
      <li><strong>Click any package</strong> to see detailed status, signer progress, the complete audit trail, and download links for signed documents once complete.</li>
      <li><strong>Void an in-flight package</strong> via the package detail page with the <em>Void Package</em> action. Voiding immediately revokes signer access and terminates the transaction.</li>
      <li><strong>Resend notifications</strong> to signers who have not responded, via the package detail page.</li>
      <li><strong>Toggle reminders</strong> on or off on an in-flight package. Reminders are the one field that can be changed after sending.</li>
      <li><strong>Archive completed packages</strong> to hide them from the default list view. Archived packages remain fully accessible under the <em>Archived</em> filter.</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Best practices</h2>
    <ul>
      <li>
        <strong>Set reasonable expiration dates.</strong> A 30 to 90 day window works
        for most signing scenarios. Too short, and signers may not complete in time.
        Too long, and stale packages clutter your dashboard.
      </li>
      <li>
        <strong>Use sequential signing order when document flow matters.</strong> For
        example, if Signer A must review and approve before Signer B receives the
        document, enable <code>enforceSigningOrder</code>. Each signer receives their
        notification only after the previous signer completes.
      </li>
      <li>
        <strong>Enable reminders to reduce turnaround time.</strong> Automated
        reminders improve completion rates. A cadence of every 3 to 5 days strikes a
        reasonable balance between urgency and notification fatigue.
      </li>
      <li>
        <strong>Validate before sending.</strong> Call the validate endpoint before
        sending to catch configuration issues early.
      </li>
      <li>
        <strong>Use webhooks instead of polling.</strong> Webhooks deliver events in
        real time and reduce unnecessary API traffic. Use polling only as a fallback.
      </li>
    </ul>

    <q-banner class="bg-blue-1 text-blue-9 q-mt-lg" rounded>
      <strong>Tip:</strong> Use the <code>create-and-send</code> shortcut (covered in
      the developer section below) for high-volume programmatic workflows. Reserve the
      multi-step draft flow for interactive use cases where the sender needs to review
      and adjust before sending.
    </q-banner>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      Everything shown above can be done programmatically via the REST API or via the MCP
      (Model Context Protocol) tools for AI-driven integrations. Each package operation has a
      dedicated endpoint and a matching MCP tool.
    </p>

    <h3>Create a package</h3>
    <p>Properties on a package (all optional except <code>name</code>):</p>
    <ul>
      <li><code>name</code>. Internal name visible to the sender in the dashboard.</li>
      <li><code>emailSubject</code> and <code>emailMessage</code>. Customization for signer notification emails.</li>
      <li><code>expirationDate</code>. ISO 8601 timestamp after which the package auto-expires.</li>
      <li><code>enforceSigningOrder</code>. When true, signers are notified in <code>routingOrder</code> sequence.</li>
      <li><code>reminderEnabled</code> and <code>reminderIntervalDays</code>. Automatic reminder cadence.</li>
    </ul>
    <GuideCodeTabs :samples="samples.createPackage" />

    <h3>Create-and-send shortcut</h3>
    <p>
      For programmatic workflows where you have everything ready upfront, one call
      creates the package, attaches documents (base64 or multipart), adds signers, and
      sends. The Draft state is skipped entirely.
    </p>
    <GuideCodeTabs :samples="samples.createAndSend" />
    <p class="text-grey-7" style="font-size: 0.9em; margin-top: -8px;">
      For an end-to-end walkthrough including the base64 encoding step, see
      <a href="/Docs/IntegrationGuides?topic=quick-start">Quick Start</a>.
    </p>

    <h3>Dry-run validation before send</h3>
    <p>
      Run the same checks the send operation runs, without actually sending. Useful
      for giving users a pre-flight "can this be sent?" status in your UI.
    </p>
    <GuideCodeTabs :samples="samples.validate" />
    <p>
      The response includes <code>isValid</code> (boolean) and, when false, an
      <code>errors</code> array describing exactly what needs to be fixed. For
      example, "signer 2 has no assigned fields" or "no documents uploaded".
    </p>

    <h3>Clone a terminal package</h3>
    <p>
      Clone a Declined, Expired, or Voided package to create a fresh Draft with the same
      documents, signers, and fields. The cloned package starts in Draft; make your
      corrections, then send.
    </p>
    <GuideCodeTabs :samples="samples.clone" />

    <h3>Archive and unarchive</h3>
    <p>
      Archived packages are hidden from the default list view but remain fully
      accessible. Archiving is a soft, reversible action. Nothing is deleted.
    </p>
    <GuideCodeTabs :samples="samples.archive" />

    <h3>Update reminders on an in-flight package</h3>
    <p>
      Reminder configuration is the only property that can be modified after a
      package is sent. All other changes require voiding and cloning.
    </p>
    <GuideCodeTabs :samples="samples.updateReminders" />

    <h3>Retrieve the audit trail</h3>
    <p>
      Read the immutable event log for a package. Every action (created, sent, viewed, signed,
      declined, voided) is recorded with timestamp, actor, IP, and user agent.
    </p>
    <GuideCodeTabs :samples="samples.auditTrail" />
    <p>
      Export formats: JSON (machine-readable), CSV (spreadsheets), PDF (printable record).
      Each event is linked via a hash chain; verify integrity with
      <code>GET /packages/{id}/audit-trail/verify</code>. Full details in
      <a href="/Docs/IntegrationGuides?topic=evidence">Evidence &amp; Compliance</a>.
    </p>

    <h3>MCP tools</h3>
    <p>
      If you are building an AI agent or integration via the Model Context Protocol, the
      following tools cover the package surface. Each tool maps directly to one of the REST
      endpoints above.
    </p>
    <q-markup-table flat bordered dense class="q-mb-md">
      <thead>
        <tr><th class="text-left">Tool</th><th class="text-left">What it does</th></tr>
      </thead>
      <tbody>
        <tr><td><code>create_package</code></td><td>Create a new Draft package</td></tr>
        <tr><td><code>create_and_send_package_json</code> / <code>create_and_send_package_multipart</code></td><td>Atomic create + send in one call</td></tr>
        <tr><td><code>list_packages</code></td><td>Paginated list with filters (status, date range, search, archived-only)</td></tr>
        <tr><td><code>get_package</code></td><td>Fetch full details for one package</td></tr>
        <tr><td><code>update_package</code></td><td>Update Draft metadata (partial update semantics)</td></tr>
        <tr><td><code>send_package</code></td><td>Transition Draft to Sent (or Scheduled if <code>scheduledSendTime</code> is provided)</td></tr>
        <tr><td><code>cancel_schedule</code></td><td>Revert a Scheduled package to Draft</td></tr>
        <tr><td><code>void_package</code></td><td>Terminate an in-flight package (rate-limited)</td></tr>
        <tr><td><code>resend_package</code></td><td>Resend notifications to all pending signers (rate-limited)</td></tr>
        <tr><td><code>validate_package</code></td><td>Dry-run the send validation</td></tr>
        <tr><td><code>clone_package</code></td><td>Clone a terminal package into a fresh Draft</td></tr>
        <tr><td><code>update_package_reminders</code></td><td>Toggle or adjust reminders on an in-flight package</td></tr>
        <tr><td><code>archive_package</code> / <code>unarchive_package</code> / <code>bulk_archive_packages</code></td><td>Archive management</td></tr>
        <tr><td><code>delete_package</code> / <code>bulk_delete_packages</code></td><td>Hard-delete drafts (terminal packages cannot be deleted, only archived)</td></tr>
        <tr><td><code>get_package_download_url</code></td><td>Pre-signed URL for the combined signed PDF</td></tr>
        <tr><td><code>get_certificate_download_url</code></td><td>Pre-signed URL for the Certificate of Completion</td></tr>
        <tr><td><code>get_audit_trail</code> / <code>export_audit_trail</code> / <code>verify_audit_integrity</code></td><td>Audit trail access and integrity verification</td></tr>
      </tbody>
    </q-markup-table>
    <p class="text-grey-7" style="font-size: 0.9em;">
      Tools are namespaced <code>mcp__esign-launchpad__*</code> in the actual tool
      catalog, for example <code>mcp__esign-launchpad__create_package</code>. Each
      tool's JSON schema matches the corresponding REST endpoint's request body.
    </p>

    <q-banner class="bg-green-1 text-green-9 q-mt-md" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Packages</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const API = 'https://api.esignlaunchpad.com';

const samples = {
    lifecycle: {
        text: `Draft --send--> Sent --viewed--> Viewed --activity--> InProgress --> Completed
   |                                                        |
   |                                                        +-- Declined (signer refuses)
   |                                                        +-- Voided   (creator cancels)
   |                                                        +-- Expired  (past expiration)
   |
   +--send with ScheduledSendTime--> Scheduled --time hit--> Sent
   +--send while docs still processing--> Queued --processing done--> Sent`
    },

    createPackage: {
        curl: `curl -X POST ${API}/api/v1/packages \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Q1 Vendor Agreement",
    "emailSubject": "Please sign the vendor agreement",
    "emailMessage": "Hi, please review and sign at your earliest convenience.",
    "expirationDate": "2026-04-15T00:00:00Z",
    "enforceSigningOrder": true,
    "reminderEnabled": true,
    "reminderIntervalDays": 3
  }'`,

        js: `const response = await fetch('${API}/api/v1/packages', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: 'Q1 Vendor Agreement',
    emailSubject: 'Please sign the vendor agreement',
    emailMessage: 'Hi, please review and sign at your earliest convenience.',
    expirationDate: '2026-04-15T00:00:00Z',
    enforceSigningOrder: true,
    reminderEnabled: true,
    reminderIntervalDays: 3
  })
});
const pkg = await response.json();`,

        node: `const response = await fetch('${API}/api/v1/packages', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'Q1 Vendor Agreement',
    emailSubject: 'Please sign the vendor agreement',
    emailMessage: 'Hi, please review and sign at your earliest convenience.',
    expirationDate: '2026-04-15T00:00:00Z',
    enforceSigningOrder: true,
    reminderEnabled: true,
    reminderIntervalDays: 3
  })
});
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);
const pkg = await response.json();`,

        python: `import os, requests

response = requests.post(
    '${API}/api/v1/packages',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'name': 'Q1 Vendor Agreement',
        'emailSubject': 'Please sign the vendor agreement',
        'emailMessage': 'Hi, please review and sign at your earliest convenience.',
        'expirationDate': '2026-04-15T00:00:00Z',
        'enforceSigningOrder': True,
        'reminderEnabled': True,
        'reminderIntervalDays': 3
    }
)
response.raise_for_status()
pkg = response.json()`,

        csharp: `var payload = new
{
    name = "Q1 Vendor Agreement",
    emailSubject = "Please sign the vendor agreement",
    emailMessage = "Hi, please review and sign at your earliest convenience.",
    expirationDate = "2026-04-15T00:00:00Z",
    enforceSigningOrder = true,
    reminderEnabled = true,
    reminderIntervalDays = 3
};
var response = await client.PostAsJsonAsync("api/v1/packages", payload);
response.EnsureSuccessStatusCode();
var pkg = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post('api/v1/packages', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'name' => 'Q1 Vendor Agreement',
        'emailSubject' => 'Please sign the vendor agreement',
        'emailMessage' => 'Hi, please review and sign at your earliest convenience.',
        'expirationDate' => '2026-04-15T00:00:00Z',
        'enforceSigningOrder' => true,
        'reminderEnabled' => true,
        'reminderIntervalDays' => 3,
    ],
]);
$pkg = json_decode($response->getBody(), true);`,

        ruby: `uri = URI('${API}/api/v1/packages')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  name: 'Q1 Vendor Agreement',
  emailSubject: 'Please sign the vendor agreement',
  emailMessage: 'Hi, please review and sign at your earliest convenience.',
  expirationDate: '2026-04-15T00:00:00Z',
  enforceSigningOrder: true,
  reminderEnabled: true,
  reminderIntervalDays: 3
}.to_json

res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
pkg = JSON.parse(res.body)`
    },

    createAndSend: {
        curl: `# Assumes agreement.pdf has been base64-encoded into $BODY_B64.
curl -X POST ${API}/api/v1/packages/create-and-send/json \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d @- <<EOF
{
  "name": "NDA - Acme Corp",
  "emailSubject": "NDA ready for your signature",
  "documents": [
    { "fileName": "nda.pdf", "documentBase64": "$BODY_B64" }
  ],
  "signers": [
    { "name": "Alice Johnson", "email": "alice@acme.com" }
  ]
}
EOF`,

        js: `const documentBase64 = await fileToBase64(file); // see Quick Start for helper
await fetch('${API}/api/v1/packages/create-and-send/json', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: 'NDA - Acme Corp',
    emailSubject: 'NDA ready for your signature',
    documents: [{ fileName: file.name, documentBase64 }],
    signers: [{ name: 'Alice Johnson', email: 'alice@acme.com' }]
  })
});`,

        node: `const fs = require('node:fs/promises');
const documentBase64 = (await fs.readFile('nda.pdf')).toString('base64');

await fetch('${API}/api/v1/packages/create-and-send/json', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'NDA - Acme Corp',
    emailSubject: 'NDA ready for your signature',
    documents: [{ fileName: 'nda.pdf', documentBase64 }],
    signers: [{ name: 'Alice Johnson', email: 'alice@acme.com' }]
  })
});`,

        python: `import base64, os, requests

with open('nda.pdf', 'rb') as f:
    document_base64 = base64.b64encode(f.read()).decode()

requests.post(
    '${API}/api/v1/packages/create-and-send/json',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'name': 'NDA - Acme Corp',
        'emailSubject': 'NDA ready for your signature',
        'documents': [{'fileName': 'nda.pdf', 'documentBase64': document_base64}],
        'signers': [{'name': 'Alice Johnson', 'email': 'alice@acme.com'}]
    }
).raise_for_status()`,

        csharp: `var documentBase64 = Convert.ToBase64String(await File.ReadAllBytesAsync("nda.pdf"));

await client.PostAsJsonAsync("api/v1/packages/create-and-send/json", new
{
    name = "NDA - Acme Corp",
    emailSubject = "NDA ready for your signature",
    documents = new[] { new { fileName = "nda.pdf", documentBase64 } },
    signers   = new[] { new { name = "Alice Johnson", email = "alice@acme.com" } }
});`,

        php: `$documentBase64 = base64_encode(file_get_contents('nda.pdf'));

$client->post('api/v1/packages/create-and-send/json', [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'name' => 'NDA - Acme Corp',
        'emailSubject' => 'NDA ready for your signature',
        'documents' => [['fileName' => 'nda.pdf', 'documentBase64' => $documentBase64]],
        'signers'   => [['name' => 'Alice Johnson', 'email' => 'alice@acme.com']],
    ],
]);`,

        ruby: `require 'base64'
document_base64 = Base64.strict_encode64(File.binread('nda.pdf'))

uri = URI('${API}/api/v1/packages/create-and-send/json')
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  name: 'NDA - Acme Corp',
  emailSubject: 'NDA ready for your signature',
  documents: [{ fileName: 'nda.pdf', documentBase64: document_base64 }],
  signers:   [{ name: 'Alice Johnson', email: 'alice@acme.com' }]
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    validate: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/validate \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/validate\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const { isValid, errors } = await response.json();`,

        node: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/validate\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const { isValid, errors } = await response.json();
if (!isValid) console.error('Cannot send yet:', errors);`,

        python: `response = requests.post(
    f'${API}/api/v1/packages/{package_id}/validate',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
result = response.json()
if not result['isValid']:
    print('Cannot send yet:', result['errors'])`,

        csharp: `var response = await client.PostAsync($"api/v1/packages/{packageId}/validate", null);
response.EnsureSuccessStatusCode();
var result = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post("api/v1/packages/{$packageId}/validate", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$result = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/validate")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
result = JSON.parse(res.body)`
    },

    clone: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/clone \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/clone\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const newPackage = await response.json();`,

        node: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/clone\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const newPackage = await response.json();
console.log('Cloned to', newPackage.id);`,

        python: `response = requests.post(
    f'${API}/api/v1/packages/{package_id}/clone',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
new_package = response.json()`,

        csharp: `var response = await client.PostAsync($"api/v1/packages/{packageId}/clone", null);
response.EnsureSuccessStatusCode();
var newPackage = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post("api/v1/packages/{$packageId}/clone", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$newPackage = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/clone")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
new_package = JSON.parse(res.body)`
    },

    archive: {
        curl: `# Archive a single terminal package
curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/archive \\
  -H "Authorization: Bearer $ESIGN_TOKEN"

# Unarchive (restore to default list view)
curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/unarchive \\
  -H "Authorization: Bearer $ESIGN_TOKEN"

# List archived packages
curl "${API}/api/v1/packages?archivedOnly=true" \\
  -H "Authorization: Bearer $ESIGN_TOKEN"

# Bulk archive (up to 50 terminal packages)
curl -X POST ${API}/api/v1/packages/bulk-archive \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{ "ids": ["guid-1", "guid-2"] }'`,

        js: `const h = { 'Authorization': 'Bearer ' + ESIGN_TOKEN };

// Archive / unarchive one
await fetch(\`${API}/api/v1/packages/\${packageId}/archive\`,   { method: 'POST', headers: h });
await fetch(\`${API}/api/v1/packages/\${packageId}/unarchive\`, { method: 'POST', headers: h });

// List archived
const archived = await (await fetch(\`${API}/api/v1/packages?archivedOnly=true\`, { headers: h })).json();

// Bulk archive
await fetch('${API}/api/v1/packages/bulk-archive', {
  method: 'POST',
  headers: { ...h, 'Content-Type': 'application/json' },
  body: JSON.stringify({ ids: ['guid-1', 'guid-2'] })
});`,

        node: `const h = { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN };

await fetch(\`${API}/api/v1/packages/\${packageId}/archive\`,   { method: 'POST', headers: h });
await fetch(\`${API}/api/v1/packages/\${packageId}/unarchive\`, { method: 'POST', headers: h });

const archived = await (await fetch(\`${API}/api/v1/packages?archivedOnly=true\`, { headers: h })).json();

await fetch('${API}/api/v1/packages/bulk-archive', {
  method: 'POST',
  headers: { ...h, 'Content-Type': 'application/json' },
  body: JSON.stringify({ ids: ['guid-1', 'guid-2'] })
});`,

        python: `h = {'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}

requests.post(f'${API}/api/v1/packages/{package_id}/archive',   headers=h).raise_for_status()
requests.post(f'${API}/api/v1/packages/{package_id}/unarchive', headers=h).raise_for_status()

archived = requests.get('${API}/api/v1/packages',
                        headers=h, params={'archivedOnly': 'true'}).json()

requests.post('${API}/api/v1/packages/bulk-archive',
              headers=h, json={'ids': ['guid-1', 'guid-2']}).raise_for_status()`,

        csharp: `await client.PostAsync($"api/v1/packages/{packageId}/archive",   null);
await client.PostAsync($"api/v1/packages/{packageId}/unarchive", null);

var archived = await client.GetFromJsonAsync<JsonElement>(
    "api/v1/packages?archivedOnly=true");

await client.PostAsJsonAsync("api/v1/packages/bulk-archive",
    new { ids = new[] { "guid-1", "guid-2" } });`,

        php: `$h = ['headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')]];

$client->post("api/v1/packages/{$packageId}/archive",   $h);
$client->post("api/v1/packages/{$packageId}/unarchive", $h);

$archived = json_decode($client->get('api/v1/packages',
    $h + ['query' => ['archivedOnly' => 'true']])->getBody(), true);

$client->post('api/v1/packages/bulk-archive',
    $h + ['json' => ['ids' => ['guid-1', 'guid-2']]]);`,

        ruby: `def api_req(verb, path, body = nil)
  uri = URI("${API}#{path}")
  req = (verb == :post ? Net::HTTP::Post : Net::HTTP::Get).new(uri)
  req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
  req['Content-Type'] = 'application/json' if body
  req.body = body.to_json if body
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
end

api_req(:post, "/api/v1/packages/#{package_id}/archive")
api_req(:post, "/api/v1/packages/#{package_id}/unarchive")
archived = JSON.parse(api_req(:get, '/api/v1/packages?archivedOnly=true').body)
api_req(:post, '/api/v1/packages/bulk-archive', { ids: ['guid-1', 'guid-2'] })`
    },

    updateReminders: {
        curl: `curl -X PATCH ${API}/api/v1/packages/$PACKAGE_ID/reminders \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{ "reminderEnabled": true, "reminderIntervalDays": 3 }'`,

        js: `await fetch(\`${API}/api/v1/packages/\${packageId}/reminders\`, {
  method: 'PATCH',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({ reminderEnabled: true, reminderIntervalDays: 3 })
});`,

        node: `await fetch(\`${API}/api/v1/packages/\${packageId}/reminders\`, {
  method: 'PATCH',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ reminderEnabled: true, reminderIntervalDays: 3 })
});`,

        python: `requests.patch(
    f'${API}/api/v1/packages/{package_id}/reminders',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'reminderEnabled': True, 'reminderIntervalDays': 3}
).raise_for_status()`,

        csharp: `var payload = new { reminderEnabled = true, reminderIntervalDays = 3 };
var response = await client.PatchAsJsonAsync(
    $"api/v1/packages/{packageId}/reminders", payload);
response.EnsureSuccessStatusCode();`,

        php: `$client->patch("api/v1/packages/{$packageId}/reminders", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['reminderEnabled' => true, 'reminderIntervalDays' => 3],
]);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/reminders")
req = Net::HTTP::Patch.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { reminderEnabled: true, reminderIntervalDays: 3 }.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    auditTrail: {
        curl: `curl ${API}/api/v1/packages/$PACKAGE_ID/audit-trail \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/audit-trail\`,
  { headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const events = await response.json();`,

        node: `const response = await fetch(
  \`${API}/api/v1/packages/\${packageId}/audit-trail\`,
  { headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const events = await response.json();`,

        python: `response = requests.get(
    f'${API}/api/v1/packages/{package_id}/audit-trail',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
events = response.json()`,

        csharp: `var events = await client.GetFromJsonAsync<JsonElement>(
    $"api/v1/packages/{packageId}/audit-trail");`,

        php: `$response = $client->get("api/v1/packages/{$packageId}/audit-trail", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$events = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/audit-trail")
req = Net::HTTP::Get.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
events = JSON.parse(res.body)`
    }
};

export default {
    name: 'GuidePackages',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>

